import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AssetMaster, CreateAssetMaster, UpdateAssetMaster } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['asset-masters', companyId] as const;

export function useAssetMasters(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () =>
      api<AssetMaster[]>(`/companies/${companyId}/asset-masters?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useAssetMaster(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['asset-masters', companyId, id],
    queryFn: () => api<AssetMaster>(`/companies/${companyId}/asset-masters/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateAssetMaster(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateAssetMaster, 'companyId'>) =>
      apiJson<typeof input, AssetMaster>('POST', `/companies/${companyId}/asset-masters`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateAssetMaster(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssetMaster }) =>
      apiJson<UpdateAssetMaster, AssetMaster>(
        'PATCH',
        `/companies/${companyId}/asset-masters/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteAssetMaster(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, AssetMaster>('DELETE', `/companies/${companyId}/asset-masters/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
